"use client";

import { TBadgeProps, Icon } from "@cloai/uikit";
import Image from "next/image";
import { useMemo } from "react";

import GiftIcon from "@/styles/image/gift-icon.webp";

import { BadgeType, Item } from "./types";

/**
 * Хук для определения и конфигурации бейджа элемента
 *
 * @function useBadge
 * @param {Item} item - Элемент, для которого нужен бейдж
 * @param {boolean} isGift - Флаг, является ли элемент подарком
 * @param {boolean} isVIP - Флаг, показывать ли элемент VIP-бейдж
 * @returns {BadgeConfig} Конфигурация бейджа
 *
 * @example
 * ```tsx
 * const { icon, className, isPing } = useBadge(item, isGift);
 *
 * return (
 *   <Badge
 *     content={icon}
 *     className={className}
 *     isPing={isPing}
 *   />
 * );
 * ```
 */
export const useBadge = (
  item: Item,
  isGift: boolean,
  isVIP: boolean,
): TBadgeProps =>
  useMemo(() => {
    /**
     * Определяем тип бейджа на основе приоритетов:
     * 1. Подарок
     * 2. Новый элемент
     * 3. Топовый элемент
     * 4. Без бейджа
     */
    const type: BadgeType = isGift
      ? "gift"
      : isVIP
        ? "vip"
        : item.isNew
          ? "new"
          : item.isTop
            ? "top"
            : "none";

    /**
     * Базовая конфигурация бейджа
     */
    const baseBadgeConfig: TBadgeProps = {
      hideOutline: true,
      size: "lg",
      classNames: { base: "size-full" },
    };

    /**
     * Конфигурация для каждого типа бейджа
     */
    const config: Record<BadgeType, TBadgeProps> = {
      // Конфигурация для подарочного бейджа
      gift: {
        ...baseBadgeConfig,
        content: (
          <Image
            src={GiftIcon}
            alt="Gift icon"
            width={20}
            height={20}
            priority
            quality={100}
          />
        ),
        isOneChar: true,
        color: "secondary",
        classNames: {
          ...baseBadgeConfig.classNames,
          badge: " p-1 h-fit",
        },
        isPing: true, // Анимация пульсации для привлечения внимания
        isInvisible: false,
      },

      // Конфигурация для VIP-бейджа
      vip: {
        ...baseBadgeConfig,
        content: (
          <Icon
            icon="diamond"
            color="primary"
            size={4}
            // className="size-auto text-[clamp(0.75rem,40cqi,6rem)] text-white/35"
          />
        ),
        isOneChar: true,
        classNames: {
          ...baseBadgeConfig.classNames,
          badge: "bg-popup",
        },
        isPing: false,
        isInvisible: false,
      },

      // Конфигурация для бейджа нового элемента
      new: {
        ...baseBadgeConfig,
        content: "NEW",
        color: "secondary",
        isPing: true,
        isInvisible: false,
      },

      // Конфигурация для бейджа топового элемента
      top: {
        ...baseBadgeConfig,
        content: "TOP",
        color: "primary",
        isPing: false,
        isInvisible: false,
      },

      // Пустая конфигурация (без бейджа)
      none: {
        ...baseBadgeConfig,
        content: null,
        isPing: false,
        isInvisible: true,
      },
    };

    // Возвращаем конфигурацию для определенного типа
    return config[type];
  }, [isGift, isVIP, item.isNew, item.isTop]);
